// src/services/DataValidationService.js
import storage from './StorageAdapter';
import auditService from './AuditService';
import entityService from './EntityService';
import geoRiskService from './GeoRiskService';
import customerRiskService from './CustomerRiskService';
import { RiskModel } from '../modules/GeoRisk/models/RiskModel';
import { calculateScore } from '../modules/CustomerRisk/utils/parameterUtils';
import { createAdapter, CSVDataAdapter } from './dataAdapters';

const RISK_LEVEL_ALIASES = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  'very high': 'Very High',
  'very_high': 'Very High',
  veryhigh: 'Very High',
  unacceptable: 'Unacceptable',
};

function normalizeRiskLevel(raw) {
  if (!raw) return null;
  const trimmed = String(raw).trim();
  // Check exact match first
  const levels = ['Low', 'Medium', 'High', 'Very High', 'Unacceptable'];
  if (levels.includes(trimmed)) return trimmed;
  // Alias lookup
  return RISK_LEVEL_ALIASES[trimmed.toLowerCase()] || null;
}

class DataValidationService {
  constructor() {
    this.DATASETS_KEY = 'validation_datasets';
    this.MAPPINGS_KEY = 'validation_mappings';
    this.RESULTS_KEY = 'validation_results';
    this.listeners = [];
    // Migrate from global keys to entity-scoped keys
    this._migrateToEntityScope();
    // Re-notify listeners when active entity changes
    if (typeof window !== 'undefined') {
      window.addEventListener('rms_active_entity_changed', () => {
        this.notifyListeners();
      });
    }
  }

  _migrateToEntityScope() {
    const migrated = storage.getItem('validation_entity_scope_migrated');
    if (migrated === 'true') return;

    const entityId = entityService.getActiveEntityId();
    if (!entityId) return;

    const jsonKeys = [this.DATASETS_KEY, this.MAPPINGS_KEY, this.RESULTS_KEY];

    for (const key of jsonKeys) {
      const globalValue = storage.getJSON(key, null);
      const scopedKey = `${key}_${entityId}`;
      const scopedValue = storage.getJSON(scopedKey, null);
      if (globalValue !== null && scopedValue === null) {
        storage.setJSON(scopedKey, globalValue);
      }
    }

    storage.setItem('validation_entity_scope_migrated', 'true');
  }

  _entityKey(baseKey) {
    const entityId = entityService.getActiveEntityId();
    return entityId ? `${baseKey}_${entityId}` : baseKey;
  }

  // === DATASETS ===

  getDatasets(module) {
    const all = storage.getJSON(this._entityKey(this.DATASETS_KEY), {});
    return all[module] || [];
  }

  async uploadData(module, csvText, label) {
    const adapter = new CSVDataAdapter({ csvText, label });
    const columnDefs = await adapter.listColumns('data');
    const rows = await adapter.query({ table: 'data' });

    if (!rows || rows.length === 0) {
      throw new Error('No valid data found in CSV');
    }

    const dataset = {
      id: String(Date.now()) + '_' + Math.random().toString(36).slice(2, 8),
      label: label || `Upload ${new Date().toISOString().split('T')[0]}`,
      columns: columnDefs.map(c => c.name),
      rows,
      rowCount: rows.length,
      timestamp: new Date().toISOString(),
    };

    const all = storage.getJSON(this._entityKey(this.DATASETS_KEY), {});
    if (!all[module]) all[module] = [];
    all[module].push(dataset);
    await storage.setJSON(this._entityKey(this.DATASETS_KEY), all);

    auditService.log('CREATE', module === 'georisk' ? 'GeoRisk' : 'CustomerRisk', {
      entity: 'ValidationDataset',
      name: dataset.label,
      rows: dataset.rowCount,
    });

    this.notifyListeners();
    return dataset;
  }

  async deleteDataset(module, datasetId) {
    const all = storage.getJSON(this._entityKey(this.DATASETS_KEY), {});
    if (all[module]) {
      all[module] = all[module].filter(d => d.id !== datasetId);
      await storage.setJSON(this._entityKey(this.DATASETS_KEY), all);
    }
    // Also remove mapping and results for this dataset
    const mappings = storage.getJSON(this._entityKey(this.MAPPINGS_KEY), {});
    const mKey = `${module}_${datasetId}`;
    if (mappings[mKey]) {
      delete mappings[mKey];
      await storage.setJSON(this._entityKey(this.MAPPINGS_KEY), mappings);
    }
    const results = storage.getJSON(this._entityKey(this.RESULTS_KEY), {});
    if (results[mKey]) {
      delete results[mKey];
      await storage.setJSON(this._entityKey(this.RESULTS_KEY), results);
    }
    this.notifyListeners();
  }

  // === COLUMN MAPPING ===

  getMapping(module, datasetId) {
    const all = storage.getJSON(this._entityKey(this.MAPPINGS_KEY), {});
    return all[`${module}_${datasetId}`] || null;
  }

  async setMapping(module, datasetId, mapping) {
    const all = storage.getJSON(this._entityKey(this.MAPPINGS_KEY), {});
    all[`${module}_${datasetId}`] = mapping;
    await storage.setJSON(this._entityKey(this.MAPPINGS_KEY), all);
    this.notifyListeners();
  }

  // === VALIDATION EXECUTION ===

  async runGeoValidation(datasetId) {
    const datasets = this.getDatasets('georisk');
    const dataset = datasets.find(d => d.id === datasetId);
    if (!dataset) throw new Error('Dataset not found');

    const mapping = this.getMapping('georisk', datasetId);
    if (!mapping || !mapping.countryCode || !mapping.expectedRiskLevel) {
      throw new Error('Column mapping incomplete: need countryCode and expectedRiskLevel');
    }

    // Get current model configuration
    const indexes = geoRiskService.getIndexes().filter(i => i.status === 'ACTIVE');
    const modelConfig = geoRiskService.getModelConfiguration();

    // Build model
    const model = new RiskModel();
    if (modelConfig) {
      model.configure(modelConfig);
    }

    // Get country data per index
    const indexDataMap = {};
    for (const index of indexes) {
      const data = geoRiskService.getCountryData(index.id);
      if (data && Array.isArray(data)) {
        for (const entry of data) {
          if (!indexDataMap[entry.alpha2]) indexDataMap[entry.alpha2] = {};
          indexDataMap[entry.alpha2][index.id] = entry.value;
        }
      }
    }

    // Use CSVDataAdapter to get rows via the unified adapter interface
    const csvAdapter = new CSVDataAdapter({ columns: dataset.columns, rows: dataset.rows });
    const dataRows = await csvAdapter.query({ table: 'data' });

    // Run validation for each row
    const results = [];
    let correct = 0;
    let total = 0;
    const confusion = {}; // { actual: { predicted: count } }
    const levels = ['Low', 'Medium', 'High', 'Very High', 'Unacceptable'];
    levels.forEach(a => {
      confusion[a] = {};
      levels.forEach(p => { confusion[a][p] = 0; });
    });

    for (const row of dataRows) {
      const countryCode = String(row[mapping.countryCode] || '').trim().toUpperCase();
      const expectedRaw = row[mapping.expectedRiskLevel];
      const expected = normalizeRiskLevel(expectedRaw);

      if (!countryCode || !expected) continue;

      // Get country data from our system
      const countryData = indexDataMap[countryCode] || {};

      // Also allow CSV to provide index values via mapping
      for (const [indexId, colName] of Object.entries(mapping.indexColumns || {})) {
        if (colName && row[colName] !== undefined && row[colName] !== '') {
          const val = row[colName];
          countryData[indexId] = val === 'true' || val === '1' ? true :
                                  val === 'false' || val === '0' ? false :
                                  isNaN(Number(val)) ? val : Number(val);
        }
      }

      const predicted = model.calculateRisk(countryData, indexes, modelConfig?.weights || {});
      const predictedLevel = predicted.level || 'Low';

      const isCorrect = predictedLevel === expected;
      if (isCorrect) correct++;
      total++;

      if (confusion[expected]) {
        confusion[expected][predictedLevel] = (confusion[expected][predictedLevel] || 0) + 1;
      }

      results.push({
        countryCode,
        expected,
        predicted: predictedLevel,
        score: predicted.score,
        correct: isCorrect,
        details: predicted.details,
      });
    }

    // Calculate metrics
    const accuracy = total > 0 ? Math.round((correct / total) * 1000) / 10 : 0;

    // False positives: model says high risk, but actual is low/medium
    const falsePositives = results.filter(r =>
      ['High', 'Very High', 'Unacceptable'].includes(r.predicted) &&
      ['Low', 'Medium'].includes(r.expected)
    );

    // False negatives: model says low/medium risk, but actual is high+
    const falseNegatives = results.filter(r =>
      ['Low', 'Medium'].includes(r.predicted) &&
      ['High', 'Very High', 'Unacceptable'].includes(r.expected)
    );

    // Distribution
    const distribution = {};
    levels.forEach(l => { distribution[l] = { expected: 0, predicted: 0 }; });
    results.forEach(r => {
      if (distribution[r.expected]) distribution[r.expected].expected++;
      if (distribution[r.predicted]) distribution[r.predicted].predicted++;
    });

    const validationResult = {
      id: String(Date.now()) + '_' + Math.random().toString(36).slice(2, 8),
      datasetId,
      datasetLabel: dataset.label,
      timestamp: new Date().toISOString(),
      total,
      correct,
      accuracy,
      falsePositives: falsePositives.length,
      falseNegatives: falseNegatives.length,
      confusion,
      distribution,
      results,
      falsePositiveDetails: falsePositives.slice(0, 20),
      falseNegativeDetails: falseNegatives.slice(0, 20),
    };

    // Store result
    const allResults = storage.getJSON(this._entityKey(this.RESULTS_KEY), {});
    const rKey = `georisk_${datasetId}`;
    if (!allResults[rKey]) allResults[rKey] = [];
    allResults[rKey].unshift(validationResult);
    // Keep last 10 runs
    if (allResults[rKey].length > 10) allResults[rKey] = allResults[rKey].slice(0, 10);
    await storage.setJSON(this._entityKey(this.RESULTS_KEY), allResults);

    auditService.log('UPDATE', 'GeoRisk', {
      entity: 'ModelValidation',
      name: dataset.label,
      accuracy: accuracy + '%',
    });

    this.notifyListeners();
    return validationResult;
  }

  async runCustomerValidation(datasetId, modelName) {
    const datasets = this.getDatasets('customerrisk');
    const dataset = datasets.find(d => d.id === datasetId);
    if (!dataset) throw new Error('Dataset not found');

    const mapping = this.getMapping('customerrisk', datasetId);
    if (!mapping || !mapping.expectedRiskLevel) {
      throw new Error('Column mapping incomplete: need expectedRiskLevel');
    }

    // Use CSVDataAdapter to get rows via the unified adapter interface
    const csvAdapter = new CSVDataAdapter({ columns: dataset.columns, rows: dataset.rows });
    const rows = await csvAdapter.query({ table: 'data' });

    const result = await this._runCustomerValidationCore({
      dataset: { ...dataset, rows },
      mapping,
      modelName,
      storeKey: `customerrisk_${datasetId}_${modelName}`,
    });

    auditService.log('UPDATE', 'CustomerRisk', {
      entity: 'ModelValidation',
      name: `${dataset.label} / ${modelName}`,
      accuracy: result.accuracy + '%',
    });

    return result;
  }

  async runCustomerValidationFromSource({ dataSourceRecord, table, mapping, modelName, filterClause }) {
    if (!dataSourceRecord) throw new Error('Data source record is required');
    if (!table) throw new Error('Table is required');
    if (!mapping || !mapping.expectedRiskLevel) {
      throw new Error('Column mapping incomplete: need expectedRiskLevel');
    }

    const adapter = createAdapter(dataSourceRecord);
    const filter = this._parseFilterClause(filterClause);

    const rows = await adapter.query({ table, filter });
    if (!rows || rows.length === 0) {
      throw new Error('No data returned from data source. Try a different table or adjust the filter.');
    }

    const columns = Object.keys(rows[0]);
    const dataset = {
      id: `src_${dataSourceRecord.id}_${table}`,
      label: `${dataSourceRecord.name} / ${table}`,
      columns,
      rows,
      rowCount: rows.length,
      timestamp: new Date().toISOString(),
    };

    const storeKey = `customerrisk_src_${dataSourceRecord.id}_${table}_${modelName}`;
    const result = await this._runCustomerValidationCore({ dataset, mapping, modelName, storeKey });

    auditService.log('UPDATE', 'CustomerRisk', {
      entity: 'ModelValidation',
      name: `${dataSourceRecord.name} / ${table} / ${modelName}`,
      accuracy: result.accuracy + '%',
      source: 'DataSource',
    });

    return result;
  }

  async _runCustomerValidationCore({ dataset, mapping, modelName, storeKey }) {
    const models = customerRiskService.getModels();
    const model = models.find(m => m.name === modelName);
    if (!model) throw new Error('Model not found: ' + modelName);

    // Model-parametrar har full config (types, riskType, weight, thresholds) inbäddade
    // efter useModelBuilder. Biblioteket används bara som fallback om någon param saknar fält.
    const allParams = customerRiskService.getParameters();
    const modelParams = (model.parameters || []).map(mp => {
      const lib = allParams.find(lp => lp.name === mp.name);
      return { ...(lib || {}), ...mp };
    });

    const config = {
      baseScore: model.configuration?.baseScore || 10,
      lowestRiskCategory: model.configuration?.lowestRiskCategory || 'NORMAL',
      riskThresholds: model.configuration?.riskThresholds || {
        lowToNormal: 2, normalToHigh: 2, highToVeryHigh: 2, veryHighToUnacceptable: 2
      },
    };

    const results = [];
    let correct = 0;
    let total = 0;

    const levels = ['Low', 'Medium', 'High', 'Very High', 'Unacceptable'];
    const confusion = {};
    levels.forEach(a => {
      confusion[a] = {};
      levels.forEach(p => { confusion[a][p] = 0; });
    });

    // Score-diff tolerance: externa score som avviker mer än detta från modellens
    // beräknade score flaggas som diskrepans (för kategoriMATCHAR-fall).
    // 20 % eller minst 30 poäng absolut — det som är störst.
    const SCORE_DIFF_PCT_THRESHOLD = 0.20;
    const SCORE_DIFF_ABS_MIN = 30;

    let scoreDiscrepancies = 0;
    let scoreExternalCount = 0;

    for (const row of dataset.rows) {
      const expectedRaw = row[mapping.expectedRiskLevel];
      const expected = normalizeRiskLevel(expectedRaw);
      if (!expected) continue;

      let totalScore = 0;
      for (const param of modelParams) {
        const paramKey = param.name || param.id;
        const colName = mapping.paramColumns?.[paramKey];
        if (!colName || row[colName] === undefined || row[colName] === '') continue;

        // types kan vara Array (modell-shape: ["Boolean"] eller ["Numerical"]) eller
        // Object (bibliotek-shape: { boolean: {...}, numeric: ... }).
        let paramType;
        if (Array.isArray(param.types)) {
          paramType = param.types[0] === 'Numerical' ? 'Numerical' : 'Boolean';
        } else if (param.types && typeof param.types === 'object') {
          paramType = param.types.numeric ? 'Numerical' : 'Boolean';
        } else {
          paramType = 'Boolean';
        }

        const rawValue = row[colName];
        let value;
        if (paramType === 'Boolean') {
          value = rawValue === 'true' || rawValue === '1' || rawValue === true;
        } else {
          value = Number(rawValue);
          if (isNaN(value)) continue;
        }

        const riskType = param.riskType || (paramType === 'Numerical' ? 'Numerical' : 'Normal');
        const weight = Number(param.weight) || 1;
        const thresholds = param.thresholds || {};

        const score = calculateScore(paramType, riskType, weight, thresholds, config, value);
        if (typeof score === 'number') {
          totalScore += score;
        }
      }

      const predictedLevel = this._scoreToRiskLevel(totalScore, config);

      const isCorrect = predictedLevel === expected;
      if (isCorrect) correct++;
      total++;

      if (confusion[expected]) {
        confusion[expected][predictedLevel] = (confusion[expected][predictedLevel] || 0) + 1;
      }

      // Externt score (valfritt — bara om användaren mappat kolumnen)
      let externalScore = null;
      let scoreDiff = null;
      let scoreDiffPct = null;
      let scoreDiscrepancy = false;
      if (mapping.expectedRiskScore) {
        const rawExt = row[mapping.expectedRiskScore];
        if (rawExt !== undefined && rawExt !== '' && rawExt !== null) {
          const parsed = Number(rawExt);
          if (!isNaN(parsed)) {
            externalScore = parsed;
            scoreExternalCount++;
            scoreDiff = Math.round((totalScore - externalScore) * 100) / 100;
            const denom = Math.max(Math.abs(externalScore), 1);
            scoreDiffPct = Math.round((scoreDiff / denom) * 1000) / 10; // % med 1 decimal
            const absDiff = Math.abs(scoreDiff);
            if (
              absDiff >= SCORE_DIFF_ABS_MIN &&
              absDiff / denom >= SCORE_DIFF_PCT_THRESHOLD
            ) {
              scoreDiscrepancy = true;
              scoreDiscrepancies++;
            }
          }
        }
      }

      results.push({
        rowIndex: dataset.rows.indexOf(row),
        expected,
        predicted: predictedLevel,
        score: Math.round(totalScore * 100) / 100,
        correct: isCorrect,
        externalScore,
        scoreDiff,
        scoreDiffPct,
        scoreDiscrepancy,
      });
    }

    const accuracy = total > 0 ? Math.round((correct / total) * 1000) / 10 : 0;

    const falsePositives = results.filter(r =>
      ['High', 'Very High', 'Unacceptable'].includes(r.predicted) &&
      ['Low', 'Medium'].includes(r.expected)
    );
    const falseNegatives = results.filter(r =>
      ['Low', 'Medium'].includes(r.predicted) &&
      ['High', 'Very High', 'Unacceptable'].includes(r.expected)
    );

    const distribution = {};
    levels.forEach(l => { distribution[l] = { expected: 0, predicted: 0 }; });
    results.forEach(r => {
      if (distribution[r.expected]) distribution[r.expected].expected++;
      if (distribution[r.predicted]) distribution[r.predicted].predicted++;
    });

    const scoreDiscrepancyDetails = results
      .filter(r => r.scoreDiscrepancy && r.correct)
      .slice(0, 20);

    const validationResult = {
      id: String(Date.now()) + '_' + Math.random().toString(36).slice(2, 8),
      datasetId: dataset.id,
      datasetLabel: dataset.label,
      modelName,
      timestamp: new Date().toISOString(),
      total,
      correct,
      accuracy,
      falsePositives: falsePositives.length,
      falseNegatives: falseNegatives.length,
      confusion,
      distribution,
      results,
      falsePositiveDetails: falsePositives.slice(0, 20),
      falseNegativeDetails: falseNegatives.slice(0, 20),
      scoreExternalCount,
      scoreDiscrepancies,
      scoreDiscrepancyDetails,
      scoreDiffThresholdPct: SCORE_DIFF_PCT_THRESHOLD * 100,
      scoreDiffThresholdAbs: SCORE_DIFF_ABS_MIN,
    };

    const allResults = storage.getJSON(this._entityKey(this.RESULTS_KEY), {});
    if (!allResults[storeKey]) allResults[storeKey] = [];
    allResults[storeKey].unshift(validationResult);
    if (allResults[storeKey].length > 10) allResults[storeKey] = allResults[storeKey].slice(0, 10);
    await storage.setJSON(this._entityKey(this.RESULTS_KEY), allResults);

    this.notifyListeners();
    return validationResult;
  }

  _parseFilterClause(filterClause) {
    if (!filterClause || !filterClause.trim()) return {};
    const filter = {};
    const pairs = filterClause.split(',');
    for (const pair of pairs) {
      const eqIdx = pair.indexOf('=');
      if (eqIdx > 0) {
        const key = pair.slice(0, eqIdx).trim();
        const value = pair.slice(eqIdx + 1).trim();
        if (key) filter[key] = value;
      }
    }
    return filter;
  }

  // Get latest validation result
  getLatestResult(module, datasetId, modelName) {
    const allResults = storage.getJSON(this._entityKey(this.RESULTS_KEY), {});
    const rKey = modelName ? `${module}_${datasetId}_${modelName}` : `${module}_${datasetId}`;
    const results = allResults[rKey] || [];
    return results[0] || null;
  }

  // Get all results for a module
  getAllResults(module) {
    const allResults = storage.getJSON(this._entityKey(this.RESULTS_KEY), {});
    const moduleResults = [];
    for (const [key, results] of Object.entries(allResults)) {
      if (key.startsWith(module + '_')) {
        moduleResults.push(...results);
      }
    }
    return moduleResults.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }

  // === PRIVATE HELPERS ===

  _scoreToRiskLevel(score, config) {
    const base = config.baseScore || 10;
    const t = config.riskThresholds || {};
    const isLow = config.lowestRiskCategory === 'LOW';

    const normalThreshold = isLow ? base * (t.lowToNormal || 2) : 0;
    const highThreshold = base * (t.normalToHigh || 2);
    const veryHighThreshold = highThreshold * (t.highToVeryHigh || 2);
    const unacceptableThreshold = veryHighThreshold * (t.veryHighToUnacceptable || 2);

    if (score >= unacceptableThreshold) return 'Unacceptable';
    if (score >= veryHighThreshold) return 'Very High';
    if (score >= highThreshold) return 'High';
    if (isLow && score <= normalThreshold) return 'Low';
    return 'Medium';
  }

  // === VALIDATION REPORT ===

  /**
   * Generate a structured validation report for a model.
   * Returns metrics, per-level analysis, and recommendations.
   */
  getValidationReport(module, datasetId, modelName) {
    const result = this.getLatestResult(module, datasetId, modelName);
    if (!result) return null;

    const levels = ['Low', 'Medium', 'High', 'Very High', 'Unacceptable'];
    const highRiskLevels = ['High', 'Very High', 'Unacceptable'];
    const lowRiskLevels = ['Low', 'Medium'];

    // Per-level accuracy (sensitivity/recall)
    const perLevelAnalysis = levels.map(level => {
      const actualCount = result.results.filter(r => r.expected === level).length;
      const correctCount = result.results.filter(r => r.expected === level && r.correct).length;
      const sensitivity = actualCount > 0 ? Math.round((correctCount / actualCount) * 1000) / 10 : null;

      const underClassified = result.results.filter(r => {
        if (r.expected !== level || r.correct) return false;
        return levels.indexOf(r.predicted) < levels.indexOf(r.expected);
      }).length;

      const overClassified = result.results.filter(r => {
        if (r.expected !== level || r.correct) return false;
        return levels.indexOf(r.predicted) > levels.indexOf(r.expected);
      }).length;

      // Precision: of all cases model predicted as this level, how many were correct
      const tp = correctCount;
      const fp = result.results.filter(r => r.predicted === level && r.expected !== level).length;
      const fn = actualCount - correctCount;
      const tn = result.total - tp - fp - fn;
      const precision = (tp + fp) > 0 ? Math.round((tp / (tp + fp)) * 1000) / 10 : null;
      const recall = sensitivity; // same as sensitivity
      const f1 = (precision !== null && recall !== null && (precision + recall) > 0)
        ? Math.round((2 * precision * recall / (precision + recall)) * 10) / 10
        : null;
      // False positive rate: of all non-level cases, how many were falsely flagged as this level
      const fpr = (fp + tn) > 0 ? Math.round((fp / (fp + tn)) * 1000) / 10 : null;

      return { level, actual: actualCount, correct: correctCount, sensitivity, underClassified, overClassified, precision, recall, f1, fpr };
    });

    // Macro averages (only for levels that have actual data)
    const levelsWithData = perLevelAnalysis.filter(a => a.actual > 0);
    const macroPrecision = levelsWithData.length > 0 && levelsWithData.every(a => a.precision !== null)
      ? Math.round(levelsWithData.reduce((s, a) => s + a.precision, 0) / levelsWithData.length * 10) / 10
      : null;
    const macroRecall = levelsWithData.length > 0 && levelsWithData.every(a => a.recall !== null)
      ? Math.round(levelsWithData.reduce((s, a) => s + a.recall, 0) / levelsWithData.length * 10) / 10
      : null;
    const macroF1 = levelsWithData.length > 0 && levelsWithData.every(a => a.f1 !== null)
      ? Math.round(levelsWithData.reduce((s, a) => s + a.f1, 0) / levelsWithData.length * 10) / 10
      : null;

    // High-risk capture rate
    const actualHighRisk = result.results.filter(r => highRiskLevels.includes(r.expected));
    const caughtHighRisk = actualHighRisk.filter(r => highRiskLevels.includes(r.predicted));
    const highRiskCaptureRate = actualHighRisk.length > 0
      ? Math.round((caughtHighRisk.length / actualHighRisk.length) * 1000) / 10
      : null;

    // False alarm rate
    const actualLowRisk = result.results.filter(r => lowRiskLevels.includes(r.expected));
    const falseAlarms = actualLowRisk.filter(r => highRiskLevels.includes(r.predicted));
    const falseAlarmRate = actualLowRisk.length > 0
      ? Math.round((falseAlarms.length / actualLowRisk.length) * 1000) / 10
      : null;

    const totalUnderClassified = perLevelAnalysis.reduce((s, a) => s + a.underClassified, 0);
    const totalOverClassified = perLevelAnalysis.reduce((s, a) => s + a.overClassified, 0);

    return {
      resultId: result.id,
      timestamp: result.timestamp,
      datasetLabel: result.datasetLabel,
      modelName: result.modelName || null,
      accuracy: result.accuracy,
      total: result.total,
      correct: result.correct,
      falsePositives: result.falsePositives,
      falseNegatives: result.falseNegatives,
      highRiskCaptureRate,
      falseAlarmRate,
      totalUnderClassified,
      totalOverClassified,
      perLevelAnalysis,
      macroPrecision,
      macroRecall,
      macroF1,
      confusion: result.confusion,
      distribution: result.distribution,
    };
  }

  /**
   * Get the latest validation summary for BWRA integration.
   */
  getLatestValidationSummary(module) {
    const allResults = storage.getJSON(this._entityKey(this.RESULTS_KEY), {});
    let latest = null;

    for (const [key, results] of Object.entries(allResults)) {
      if (key.startsWith(module + '_') && results.length > 0) {
        const r = results[0];
        if (!latest || new Date(r.timestamp) > new Date(latest.timestamp)) {
          latest = r;
        }
      }
    }

    if (!latest) return null;

    const highRiskLevels = ['High', 'Very High', 'Unacceptable'];
    const actualHigh = (latest.results || []).filter(r => highRiskLevels.includes(r.expected));
    const caughtHigh = actualHigh.filter(r => highRiskLevels.includes(r.predicted));
    const highRiskCaptureRate = actualHigh.length > 0
      ? Math.round((caughtHigh.length / actualHigh.length) * 1000) / 10
      : null;

    return {
      accuracy: latest.accuracy,
      highRiskCaptureRate,
      timestamp: latest.timestamp,
      datasetLabel: latest.datasetLabel,
      modelName: latest.modelName || null,
      total: latest.total,
    };
  }

  /**
   * Get validation summary for a specific model (for BWRA model score integration).
   */
  getModelValidationSummary(module, modelName) {
    const allResults = storage.getJSON(this._entityKey(this.RESULTS_KEY), {});
    let latest = null;

    for (const [key, results] of Object.entries(allResults)) {
      if (!key.startsWith(module + '_') || results.length === 0) continue;
      const r = results[0];
      // For customerrisk, key format is customerrisk_datasetId_modelName
      if (module === 'customerrisk' && modelName) {
        if (!key.endsWith('_' + modelName)) continue;
      }
      if (!latest || new Date(r.timestamp) > new Date(latest.timestamp)) {
        latest = r;
      }
    }

    if (!latest) return null;

    const highRiskLevels = ['High', 'Very High', 'Unacceptable'];
    const actualHigh = (latest.results || []).filter(r => highRiskLevels.includes(r.expected));
    const caughtHigh = actualHigh.filter(r => highRiskLevels.includes(r.predicted));
    const highRiskCaptureRate = actualHigh.length > 0
      ? Math.round((caughtHigh.length / actualHigh.length) * 1000) / 10
      : null;

    return {
      accuracy: latest.accuracy,
      highRiskCaptureRate,
      timestamp: latest.timestamp,
      total: latest.total,
    };
  }

  // === CHANGE LISTENERS ===

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  notifyListeners() {
    this.listeners.forEach(listener => listener());
  }
}

const dataValidationService = new DataValidationService();

if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.dataValidationService = dataValidationService;
}

export default dataValidationService;
